import React, { useState, useRef } from 'react';
import api from '../api';

const BasicRedactionModal = ({ docId, docTitle, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rects, setRects] = useState([]);
  const [drawing, setDrawing] = useState(null);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);

  const handleMouseDown = (e) => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    
    // Enable pointer events while drawing
    overlay.style.pointerEvents = 'auto';
    
    const rect = overlay.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    setDrawing({ startX: x, startY: y });
  };

  const handleMouseMove = (e) => {
    if (!drawing || !overlayRef.current) return;

    const rect = overlayRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Draw temporary rectangle on overlay
    const ctx = overlayRef.current.getContext('2d');
    overlayRef.current.width = rect.width;
    overlayRef.current.height = rect.height;
    
    // Redraw existing rectangles
    rects.forEach(r => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
      ctx.fillRect(r.x, r.y, r.width, r.height);
    });
    
    // Draw current rectangle
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(
      drawing.startX,
      drawing.startY,
      x - drawing.startX,
      y - drawing.startY
    );
  };

  const handleMouseUp = (e) => {
    if (!drawing || !overlayRef.current) return;

    const rect = overlayRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const width = x - drawing.startX;
    const height = y - drawing.startY;

    if (Math.abs(width) > 10 && Math.abs(height) > 10) {
      const newRect = {
        x: Math.min(drawing.startX, x),
        y: Math.min(drawing.startY, y),
        width: Math.abs(width),
        height: Math.abs(height)
      };

      const newRects = [...rects, newRect];
      setRects(newRects);
      
      // Redraw all rectangles
      const ctx = overlayRef.current.getContext('2d');
      newRects.forEach(r => {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.fillRect(r.x, r.y, r.width, r.height);
      });
    }

    // Disable pointer events after drawing to allow scrolling
    overlayRef.current.style.pointerEvents = 'none';
    setDrawing(null);
  };

  const handleSaveRedacted = async () => {
    if (rects.length === 0) {
      alert('Please draw at least one redaction rectangle');
      return;
    }

    try {
      setLoading(true);
      await api.post('/api/basic-redaction/create', {
        source_doc_id: docId,
        rects: rects,
        visible_to: 'public'
      });

      alert('✅ Redaction saved! Document can now be viewed with redactions.');
      onClose();
    } catch (err) {
      console.error('Error saving redaction:', err);
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => d.msg || JSON.stringify(d)).join('\n')
        : (detail || err.message);
      alert('Error saving redaction:\n' + msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = () => {
    setRects([]);
    if (overlayRef.current) {
      const ctx = overlayRef.current.getContext('2d');
      ctx.clearRect(0, 0, overlayRef.current.width, overlayRef.current.height);
    }
  };

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-6 max-w-2xl w-full">
          <h3 className="text-xl font-semibold mb-4 text-red-600">Error</h3>
          <p className="text-gray-700 mb-4">{error}</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-6xl w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">🔒 Simple Redaction - {docTitle}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <p className="text-sm text-purple-900">
            <strong>✨ NEW - Simple Redaction Tool:</strong> Click and drag to draw black rectangles. 
            No PDF conversion needed - direct document viewing! v2.0
          </p>
        </div>

        {/* Document Viewer with Overlay */}
        <div className="flex-1 relative border-2 border-gray-300 rounded-lg overflow-auto bg-gray-100 max-h-[70vh]">
          <div
            ref={containerRef}
            className="relative w-full h-full"
            style={{ cursor: 'crosshair' }}
          >
            {/* Embedded document viewer - NO CONVERSION */}
            <iframe
              src={`/api/documents/${docId}/view#toolbar=0&navpanes=0`}
              className="w-full h-full"
              title={docTitle}
            />
            
            {/* Canvas overlay for drawing rectangles */}
            <canvas
              ref={overlayRef}
              className="absolute top-0 left-0"
              style={{ 
                width: '100%', 
                height: '100%',
                pointerEvents: drawing ? 'auto' : 'none'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
            />
          </div>
        </div>

        <div className="mt-4 flex gap-2 justify-end">
          <div className="flex-1 text-sm text-gray-600">
            {rects.length} redaction(s) drawn
          </div>
          <button
            onClick={handleClearAll}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Clear All
          </button>
          <button
            onClick={handleSaveRedacted}
            disabled={rects.length === 0 || loading}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
          >
            {loading ? 'Saving...' : 'Save Redaction'}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default BasicRedactionModal;
